import React, { useState, useEffect, useCallback } from 'react';
import { Container, Table, Button, Badge, Modal, Form, Alert, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash, faFolderOpen, faDownload } from '@fortawesome/free-solid-svg-icons';
import { getCopertineLotti, creaCopertineLotto, eliminaCopertineLotto } from '../services/api';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const ESEMPIO = `1.Shut in (D.J. Caruso)
2.Pensive (Jonas Trukanas)
3.Il coltello/Noz (Z. Mitrovic)
4.Midnight Mass (miniserie Tv)`;

function CopertineDvdList() {
  const navigate = useNavigate();
  const [lotti, setLotti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showNuovo, setShowNuovo] = useState(false);
  const [nome, setNome] = useState('');
  const [testo, setTesto] = useState('');
  const [creando, setCreando] = useState(false);

  const carica = useCallback(async () => {
    try {
      const res = await getCopertineLotti();
      setLotti(res.data);
    } catch (err) {
      setError('Errore durante il caricamento dei lotti.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { carica(); }, [carica]);

  const crea = async () => {
    if (!testo.trim()) {
      setError('Incolla l\'elenco dei film.');
      return;
    }
    try {
      setCreando(true);
      setError(null);
      const res = await creaCopertineLotto(nome.trim() || 'Nuovo lotto', testo);
      navigate(`/copertine-dvd/${res.data.id}`);
    } catch (err) {
      setError('Impossibile creare il lotto: ' + (err.response?.data?.detail || err.message));
    } finally {
      setCreando(false);
    }
  };

  const elimina = async (lotto) => {
    if (!window.confirm(`Eliminare il lotto "${lotto.nome}" con tutte le sue copertine?`)) return;
    try {
      await eliminaCopertineLotto(lotto.id);
      carica();
    } catch (err) {
      setError('Eliminazione non riuscita.');
    }
  };

  if (loading) {
    return <Container className="text-center mt-5"><Spinner animation="border" /></Container>;
  }

  return (
    <Container fluid className="p-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h2 className="mb-0">Copertine DVD</h2>
          <div className="text-muted">Copertine cartacee per custodia DVD, da un elenco di film. Incolla la mail del cliente e controlla i risultati.</div>
        </div>
        <Button onClick={() => setShowNuovo(true)}><FontAwesomeIcon icon={faPlus} /> Nuovo lotto</Button>
      </div>

      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

      {lotti.length === 0 ? (
        <Alert variant="info">Nessun lotto. Clicca su "Nuovo lotto" e incolla l'elenco dei film che ti ha mandato il cliente.</Alert>
      ) : (
        <Table hover responsive className="align-middle">
          <thead>
            <tr><th>Lotto</th><th>Creato il</th><th>Film</th><th>Stato</th><th>PDF</th><th /></tr>
          </thead>
          <tbody>
            {lotti.map(l => (
              <tr key={l.id}>
                <td><strong>{l.nome}</strong></td>
                <td>{l.created_at ? new Date(l.created_at).toLocaleDateString('it-IT') : '-'}</td>
                <td>{l.totale}</td>
                <td>
                  {l.stato_ricerca === 'in_corso' && <Badge bg="info" className="me-1">Ricerca in corso…</Badge>}
                  <Badge bg="success" className="me-1">{l.trovati} trovati</Badge>
                  {l.dubbi > 0 && <Badge bg="warning" text="dark" className="me-1">{l.dubbi} dubbi</Badge>}
                  {l.non_trovati > 0 && <Badge bg="danger">{l.non_trovati} non trovati</Badge>}
                </td>
                <td>
                  {l.pdf_stato === 'generating' && <span className="text-warning"><Spinner size="sm" className="me-1" />Generazione…</span>}
                  {l.pdf_stato === 'ok' && l.pdf_path && (
                    <Button size="sm" variant="outline-success" onClick={() => window.open(`${API_BASE}${l.pdf_path}?t=${Date.now()}`, '_blank')}>
                      <FontAwesomeIcon icon={faDownload} /> PDF
                    </Button>
                  )}
                </td>
                <td className="text-end">
                  <Button size="sm" variant="outline-primary" className="me-2" onClick={() => navigate(`/copertine-dvd/${l.id}`)}>
                    <FontAwesomeIcon icon={faFolderOpen} /> Apri
                  </Button>
                  <Button size="sm" variant="outline-danger" onClick={() => elimina(l)} title="Elimina il lotto"><FontAwesomeIcon icon={faTrash} /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <Modal show={showNuovo} onHide={() => setShowNuovo(false)} size="lg">
        <Modal.Header closeButton><Modal.Title>Nuovo lotto di copertine</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Nome del lotto</Form.Label>
            <Form.Control value={nome} onChange={(e) => setNome(e.target.value)} placeholder="es. Stefano - ottobre 2026" />
          </Form.Group>
          <Form.Group>
            <Form.Label>Elenco dei film (incolla il testo della mail)</Form.Label>
            <Form.Control
              as="textarea" rows={14} value={testo} onChange={(e) => setTesto(e.target.value)}
              placeholder={ESEMPIO} style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}
            />
            <Form.Text className="text-muted">
              Una riga per film. Il regista va tra parentesi; per le serie scrivi "(miniserie Tv)"; con due titoli separali con "/". I numeri iniziali e le righe vuote vengono ignorati.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowNuovo(false)}>Annulla</Button>
          <Button onClick={crea} disabled={creando}>
            {creando ? <><Spinner size="sm" animation="border" /> Creo…</> : 'Crea e cerca i film'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default CopertineDvdList;
